import { isSyntheticRoomCamPlaceholder } from '@/helpers/preview/room-cam-tile.helper';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { OutputLayer } from '@/components/preview/output-layer-preview';
import type { FlowControlPerformMode, OutputPreviewTab } from '@/types/player/simulation-cue-sheet.types';

type ResolveOutputPreviewLayersInput = {
  performMode: FlowControlPerformMode;
  tab: OutputPreviewTab;
  simulationLayers?: OutputLayer[];
  liveContextState?: LiveContextStateDto;
  /** Physical player Mongo ids for the active preview tab — avoids merging stage/online snapshots. */
  displayPlayerIds?: string[];
  /** Official backup-slides episode — Current must hide leftover LiveKit like the player. */
  officialBackupSlides?: boolean;
};

const PREVIEW_KEY_BY_TAB: Record<OutputPreviewTab, string> = {
  stageRoom: '_preview:stagePlayer',
  live: '_preview:onlinePlayer',
};

const LOOKAHEAD_KEY_BY_TAB: Record<OutputPreviewTab, string> = {
  stageRoom: '_lookahead:stagePlayer',
  live: '_lookahead:onlinePlayer',
};

const LOOKAHEAD2_KEY_BY_TAB: Record<OutputPreviewTab, string> = {
  stageRoom: '_lookahead2:stagePlayer',
  live: '_lookahead2:onlinePlayer',
};

const LOOKAHEAD_RESUME_KEY_BY_TAB: Record<OutputPreviewTab, string> = {
  stageRoom: '_lookaheadResume:stagePlayer',
  live: '_lookaheadResume:onlinePlayer',
};

const PLAYER_TYPE_BY_TAB: Record<OutputPreviewTab, string> = {
  stageRoom: 'stagePlayer',
  live: 'onlinePlayer',
};

export function isPreviewByPlayerKey(playerKey: string): boolean {
  return playerKey === '_preview' || playerKey.startsWith('_preview:');
}

/** Control Next snapshots pushed on `live-context-play-state`. Not `_lookahead2` / resume. */
export function isLookAheadByPlayerKey(playerKey: string): boolean {
  return playerKey === '_lookahead' || playerKey.startsWith('_lookahead:');
}

export function isLookAhead2ByPlayerKey(playerKey: string): boolean {
  return playerKey === '_lookahead2' || playerKey.startsWith('_lookahead2:');
}

export function isLookAheadResumeByPlayerKey(playerKey: string): boolean {
  return playerKey === '_lookaheadResume' || playerKey.startsWith('_lookaheadResume:');
}

export function isLookAheadPrevByPlayerKey(playerKey: string): boolean {
  return playerKey === '_lookaheadPrev' || playerKey.startsWith('_lookaheadPrev:');
}

export function isOperatorLookAheadByPlayerKey(playerKey: string): boolean {
  return (
    isLookAheadByPlayerKey(playerKey) ||
    isLookAhead2ByPlayerKey(playerKey) ||
    isLookAheadResumeByPlayerKey(playerKey) ||
    isLookAheadPrevByPlayerKey(playerKey)
  );
}

export function isOperatorPlayStateByPlayerKey(playerKey: string): boolean {
  return isPreviewByPlayerKey(playerKey) || isOperatorLookAheadByPlayerKey(playerKey);
}

/**
 * Player preload rows are stored hidden. Control Next must show composite HTML that is already
 * in the snapshot; otherwise the frame stays black until a later click refetches it.
 */
function revealPreloadedHtmlForOperatorPreview(layers: OutputLayer[]): OutputLayer[] {
  return layers.map((layer) => {
    if (layer.componentId !== 'html' || !String(layer.data?.html ?? '').trim()) {
      return layer;
    }
    if (layer.visible !== false && layer.preload !== true) {
      return layer;
    }
    return { ...layer, visible: true, preload: false };
  });
}

function lookAheadLayersForTab(liveContextState: LiveContextStateDto | undefined, key: string): OutputLayer[] {
  const layers = ((liveContextState?.playState?.byPlayer?.[key]?.layers ?? []) as OutputLayer[]).slice();
  return revealPreloadedHtmlForOperatorPreview(layers);
}

/** Layers for the row after Next from BE-pushed `_lookahead2:*`. */
export function resolveLookAhead2PreviewLayers(
  liveContextState: LiveContextStateDto | undefined,
  tab: OutputPreviewTab,
): OutputLayer[] {
  return lookAheadLayersForTab(liveContextState, LOOKAHEAD2_KEY_BY_TAB[tab]);
}

/** Layers for Control Next from BE-pushed `_lookahead:*` (empty when missing). */
export function resolveLookAheadPreviewLayers(
  liveContextState: LiveContextStateDto | undefined,
  tab: OutputPreviewTab,
): OutputLayer[] {
  return lookAheadLayersForTab(liveContextState, LOOKAHEAD_KEY_BY_TAB[tab]);
}

/** Insert overlay: pre-insert Current copied to `_lookaheadResume:*`. */
export function resolveLookAheadResumePreviewLayers(
  liveContextState: LiveContextStateDto | undefined,
  tab: OutputPreviewTab,
): OutputLayer[] {
  return lookAheadLayersForTab(liveContextState, LOOKAHEAD_RESUME_KEY_BY_TAB[tab]);
}

/** Whether look-ahead / resume meta is the same cue identity as Control Next. */
export function lookAheadMetaMatchesNextTarget(
  meta: { itemId?: string; slotId?: string; scope?: string } | null | undefined,
  next: { itemId?: string; slotId?: string; scope?: string } | null | undefined,
): boolean {
  if (!meta?.itemId || !next?.itemId || meta.itemId !== next.itemId) {
    return false;
  }
  if (meta.slotId && next.slotId && meta.slotId !== next.slotId) {
    return false;
  }
  if (meta.scope && next.scope && meta.scope !== next.scope) {
    return false;
  }
  return true;
}

/** Identity stamped with `_lookahead:*` — FE must match this to cue Next before painting. */
export function resolveLookAheadPreviewMeta(
  liveContextState: LiveContextStateDto | undefined,
): { itemId: string; showMode?: string; scope?: string; slotId?: string } | null {
  const meta = liveContextState?.playState?.lookAheadPreview;
  if (!meta?.itemId) {
    return null;
  }
  return {
    itemId: meta.itemId,
    showMode: meta.showMode,
    scope: meta.scope,
    slotId: meta.slotId,
  };
}

/** Prefer rows with track SIDs, slide metadata, and on-air visibility when merging stale `_preview` snapshots. */
export function layerCompletenessScore(layer: OutputLayer): number {
  let score = 0;
  if (layer.visible !== false) {
    score += 4;
  }
  if (layer.preload === true) {
    score += 1;
  }
  if (collectOutputLayerTrackSids(layer).length > 0) {
    score += 10;
  }
  const hasSlideMeta =
    Boolean(layer.data?.slideCount) ||
    Boolean(layer.data?.frameCount) ||
    Boolean(layer.data?.imageSequenceUrls) ||
    Boolean(layer.data?.imageUrls);
  if (hasSlideMeta) {
    score += 6;
  }
  if (Object.keys(layer.data ?? {}).length > 0) {
    score += 1;
  }
  return score;
}

/** True when position is the layer-builder fallback (no template slot). */
export function isFullscreenFallbackPosition(pos: OutputLayer['position']): boolean {
  return pos.x === 0 && pos.y === 0 && pos.w >= 100 && pos.h >= 100;
}

function preferLayoutLayer(a: OutputLayer, b: OutputLayer): OutputLayer {
  const aFull = isFullscreenFallbackPosition(a.position);
  const bFull = isFullscreenFallbackPosition(b.position);
  if (aFull && !bFull) {
    return b;
  }
  if (bFull && !aFull) {
    return a;
  }
  return a;
}

function mergeTwoOutputLayers(existing: OutputLayer, incoming: OutputLayer): OutputLayer {
  const existingScore = layerCompletenessScore(existing);
  const incomingScore = layerCompletenessScore(incoming);
  const [richer, other] = incomingScore >= existingScore ? [incoming, existing] : [existing, incoming];
  const layout = preferLayoutLayer(richer, other);
  const trackSids = [...new Set([...collectOutputLayerTrackSids(richer), ...collectOutputLayerTrackSids(other)])];

  return {
    ...richer,
    layerId: layout.layerId,
    position: layout.position,
    cutLeft: layout.cutLeft,
    cutRight: layout.cutRight,
    trackSids: trackSids.length > 0 ? trackSids : undefined,
    trackSid: richer.trackSid ?? other.trackSid,
  };
}

function layerItemKey(layer: OutputLayer): string | null {
  if (!layer.itemId) {
    return null;
  }
  return `${layer.componentId}:${layer.itemId}`;
}

/** Collapse duplicate renderable rows for the same item (e.g. stage fullscreen + online PiP at different layerIds). */
export function dedupeOutputPreviewLayersByItem(layers: OutputLayer[]): OutputLayer[] {
  const byItem = new Map<string, OutputLayer>();
  const passthrough: OutputLayer[] = [];
  for (const layer of layers) {
    const key = layerItemKey(layer);
    if (!key) {
      passthrough.push(layer);
      continue;
    }
    const existing = byItem.get(key);
    if (!existing) {
      byItem.set(key, layer);
      continue;
    }
    byItem.set(key, mergeTwoOutputLayers(existing, layer));
  }
  return [...passthrough, ...byItem.values()].sort((a, b) => a.layerId - b.layerId);
}

/** Merge layer lists by `layerId`, keeping the richest row per id. */
export function mergeOutputPreviewLayersByLayerId(...layerGroups: OutputLayer[][]): OutputLayer[] {
  const byId = new Map<number, OutputLayer>();
  for (const group of layerGroups) {
    for (const layer of group) {
      const existing = byId.get(layer.layerId);
      if (!existing) {
        byId.set(layer.layerId, layer);
        continue;
      }
      byId.set(layer.layerId, mergeTwoOutputLayers(existing, layer));
    }
  }
  return dedupeOutputPreviewLayersByItem([...byId.values()]);
}

/** Physical display-player snapshots (Mongo player ids) — often fresher than `_preview:*` after reload. */
export function resolveDisplayPlayoutLayersFromByPlayer(
  byPlayer: NonNullable<LiveContextStateDto['playState']>['byPlayer'] | undefined,
  displayPlayerIds?: string[],
): OutputLayer[] {
  if (!byPlayer || displayPlayerIds == null || displayPlayerIds.length === 0) {
    return [];
  }
  const groups: OutputLayer[][] = [];
  for (const [playerKey, entry] of Object.entries(byPlayer)) {
    if (isOperatorPlayStateByPlayerKey(playerKey) || !entry?.layers?.length) {
      continue;
    }
    if (!displayPlayerIds.includes(playerKey)) {
      continue;
    }
    groups.push(entry.layers as OutputLayer[]);
  }
  return mergeOutputPreviewLayersByLayerId(...groups);
}

const LIVEKIT_PREVIEW_COMPONENT_IDS = new Set([
  'livekitTrackScreenshare',
  'livekitTrackMediaAgent',
  'livekitTrackRoomCam',
  'livekitTrackRemoteSpeaker',
]);

/**
 * Static/media layers that define on-air composition. When `_preview:*` already carries these,
 * physical-player rows must not add a second generation of the same content (stale GET HTML/video
 * stacked on top of a fresher websocket preview).
 */
const PREVIEW_CONTENT_COMPONENT_IDS = new Set([
  'html',
  'video',
  'backgroundVideo',
  'image',
  'imageSequence',
  'presentationSlides',
  'pdf',
  'audio',
]);

export function collectOutputLayerTrackSids(layer: Pick<OutputLayer, 'trackSid' | 'trackSids'>): string[] {
  const fromList = (layer.trackSids ?? []).filter((s): s is string => Boolean(s));
  const fromSingle = layer.trackSid ? [layer.trackSid] : [];
  return [...new Set([...fromList, ...fromSingle])];
}

export function isLiveKitPreviewLayer(layer: OutputLayer): boolean {
  return LIVEKIT_PREVIEW_COMPONENT_IDS.has(layer.componentId);
}

/**
 * LiveKit SIDs from any physical display player. STAGE/ROOM / BROADCAST layout stays `_preview:*`;
 * the matching player type does not need to be connected for the media-agent track to attach.
 */
export function collectPhysicalLiveKitSidDonorLayers(
  byPlayer: NonNullable<LiveContextStateDto['playState']>['byPlayer'] | undefined,
): OutputLayer[] {
  if (!byPlayer) {
    return [];
  }
  const donors: OutputLayer[] = [];
  for (const [playerKey, entry] of Object.entries(byPlayer)) {
    if (isOperatorPlayStateByPlayerKey(playerKey) || !entry?.layers?.length) {
      continue;
    }
    for (const layer of entry.layers as OutputLayer[]) {
      if (!isLiveKitPreviewLayer(layer)) {
        continue;
      }
      if (collectOutputLayerTrackSids(layer).length === 0) {
        continue;
      }
      donors.push(layer);
    }
  }
  return donors;
}

export function previewDefinesContentComposition(layers: OutputLayer[]): boolean {
  return layers.some((layer) => layer.visible !== false && PREVIEW_CONTENT_COMPONENT_IDS.has(layer.componentId));
}

/** Visible media that can actually paint. An empty image or html shell does not count. */
const USABLE_SOURCE_KEYS: Record<string, readonly string[]> = {
  html: ['html'],
  image: ['src', 'imageUrl', 'url'],
  video: ['videoUrl', 'url'],
  backgroundVideo: ['videoUrl', 'url'],
  audio: ['audioUrl', 'url'],
  pdf: ['pdfUrl', 'url', 'src'],
  imageSequence: ['imageSequenceUrls', 'imageUrls', 'slidePreviewUrls'],
  presentationSlides: ['imageSequenceUrls', 'imageUrls', 'slidePreviewUrls'],
};

function dataValueIsUsableSource(key: string, value: string | undefined): boolean {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return false;
  }
  if (key === 'imageUrls' || key === 'imageSequenceUrls' || key === 'slidePreviewUrls') {
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) && parsed.some((entry) => typeof entry === 'string' && entry.length > 0);
    } catch {
      return false;
    }
  }
  return true;
}

/** True when this layer has a source the preview can paint, not only slide counts or an empty URL list. */
export function contentLayerHasUsableSource(layer: OutputLayer): boolean {
  const keys = USABLE_SOURCE_KEYS[layer.componentId];
  if (!keys) {
    return false;
  }
  return keys.some((key) => dataValueIsUsableSource(key, layer.data?.[key]));
}

export function previewShowsRenderableContent(layers: OutputLayer[]): boolean {
  return layers.some((layer) => layer.visible !== false && contentLayerHasUsableSource(layer));
}

/**
 * Keep preview layout/composition; copy LiveKit track SIDs from display onto matching preview rows.
 * Never invent extra content layers from a stale physical snapshot, and never let display
 * visibility resurrect a preview row the websocket already hid (agent → PDF / hide push).
 */
function displaySidDonorMatches(preview: OutputLayer, display: OutputLayer): boolean {
  if (preview.componentId !== display.componentId) {
    return false;
  }
  if (preview.itemId && display.itemId) {
    return preview.itemId === display.itemId;
  }
  return preview.layerId === display.layerId && !preview.itemId && !display.itemId;
}

export function enrichPreviewLayersWithDisplayTrackSids(
  previewLayers: OutputLayer[],
  displayLayers: OutputLayer[],
): OutputLayer[] {
  if (displayLayers.length === 0) {
    return previewLayers;
  }
  return previewLayers.map((layer) => {
    if (!isLiveKitPreviewLayer(layer)) {
      return layer;
    }
    const donor = displayLayers.find((candidate) => displaySidDonorMatches(layer, candidate));
    if (!donor) {
      return layer;
    }
    const trackSids = [...new Set([...collectOutputLayerTrackSids(layer), ...collectOutputLayerTrackSids(donor)])];
    return {
      ...layer,
      trackSids: trackSids.length > 0 ? trackSids : undefined,
      trackSid: layer.trackSid ?? donor.trackSid,
    };
  });
}

/**
 * A show-mode preview is built without the live publication. Copy the camera that is already
 * on screen onto that layout so the track is not replaced by a placeholder spinner.
 * A slot that names a real camera or speaker keeps that identity. Only a synthetic placeholder
 * may borrow a track that no named slot already claimed.
 */
export function preserveLiveKitTracksFromCurrent(
  nextLayers: OutputLayer[],
  currentLayers: OutputLayer[],
): OutputLayer[] {
  const donors = currentLayers.filter(
    (layer) => isLiveKitPreviewLayer(layer) && layer.visible !== false && collectOutputLayerTrackSids(layer).length > 0,
  );
  if (donors.length === 0) {
    return nextLayers;
  }
  const used = new Set<OutputLayer>();
  const needsTrack = (layer: OutputLayer) =>
    isLiveKitPreviewLayer(layer) && layer.visible !== false && collectOutputLayerTrackSids(layer).length === 0;
  const isPlaceholder = (layer: OutputLayer) => (layer.itemId ?? '').includes('placeholder');

  const claim = (layer: OutputLayer, donor: OutputLayer | undefined): OutputLayer => {
    if (!donor) {
      return layer;
    }
    used.add(donor);
    return {
      ...layer,
      itemId: isPlaceholder(layer) ? (donor.itemId ?? layer.itemId) : layer.itemId,
      trackSid: donor.trackSid,
      trackSids: donor.trackSids,
    };
  };

  const reserved = nextLayers.map((layer) => {
    if (!needsTrack(layer) || isPlaceholder(layer) || !layer.itemId) {
      return layer;
    }
    const donor = donors.find(
      (candidate) =>
        candidate.componentId === layer.componentId && candidate.itemId === layer.itemId && !used.has(candidate),
    );
    return claim(layer, donor);
  });

  return reserved.map((layer) => {
    if (!needsTrack(layer) || !isPlaceholder(layer)) {
      return layer;
    }
    const donor = donors.find((candidate) => candidate.componentId === layer.componentId && !used.has(candidate));
    return claim(layer, donor);
  });
}

/**
 * A show-mode preview can describe the new layout before its media URLs exist.
 * Keep the picture that is already on screen for the same item.
 */
export function preserveContentMediaFromCurrent(
  nextLayers: OutputLayer[],
  currentLayers: OutputLayer[],
): OutputLayer[] {
  return nextLayers.map((layer) => {
    if (isLiveKitPreviewLayer(layer) || layer.visible === false || contentLayerHasUsableSource(layer)) {
      return layer;
    }
    const donor = currentLayers.find(
      (candidate) =>
        !isLiveKitPreviewLayer(candidate) &&
        candidate.componentId === layer.componentId &&
        candidate.itemId != null &&
        candidate.itemId === layer.itemId &&
        contentLayerHasUsableSource(candidate),
    );
    if (!donor) {
      return layer;
    }
    const data = { ...layer.data };
    for (const key of USABLE_SOURCE_KEYS[layer.componentId] ?? []) {
      const donorValue = donor.data?.[key];
      if (
        typeof donorValue !== 'string' ||
        !dataValueIsUsableSource(key, donorValue) ||
        dataValueIsUsableSource(key, data[key])
      ) {
        continue;
      }
      data[key] = donorValue;
    }
    return { ...layer, data };
  });
}

export function resolveOutputPreviewLayers({
  performMode,
  tab,
  simulationLayers,
  liveContextState,
  displayPlayerIds,
  officialBackupSlides = false,
}: ResolveOutputPreviewLayersInput): OutputLayer[] {
  if (performMode === 'simulation') {
    return simulationLayers ?? [];
  }

  const byPlayer = liveContextState?.playState?.byPlayer;
  const previewKey = PREVIEW_KEY_BY_TAB[tab];
  const previewLayers = (byPlayer?.[previewKey]?.layers ?? []) as OutputLayer[];
  const displayLayers = resolveDisplayPlayoutLayersFromByPlayer(byPlayer, displayPlayerIds);
  const sidDonorLayers = collectPhysicalLiveKitSidDonorLayers(byPlayer);
  const topLevelLayers = (liveContextState?.playState?.layers ?? []) as OutputLayer[];

  let resolved: OutputLayer[];
  // Fresher `_preview:*` (e.g. live-context-play-state WS) already has the on-air media. Unioning
  // physical-player rows from a lagging GET reintroduces the previous HTML/video as a second layer.
  if (previewLayers.length > 0 && previewDefinesContentComposition(previewLayers)) {
    resolved = enrichPreviewLayersWithDisplayTrackSids(previewLayers, sidDonorLayers);
  } else if (previewLayers.length > 0 && liveContextState?.playState?.presentationTransitionInFlight === true) {
    // During agent-track hide (play-once → PDF), preview may only contain hidden LiveKit rows — not
    // "content" composition yet. Still prefer that snapshot; merging display re-shows the stale track.
    resolved = enrichPreviewLayersWithDisplayTrackSids(previewLayers, sidDonorLayers);
  } else if (previewLayers.some((layer) => layer.visible !== false && isLiveKitPreviewLayer(layer))) {
    resolved = enrichPreviewLayersWithDisplayTrackSids(previewLayers, sidDonorLayers);
  } else {
    const merged = mergeOutputPreviewLayersByLayerId(previewLayers, displayLayers);
    if (merged.length > 0) {
      resolved = merged;
    } else {
      const legacyPreview = (byPlayer?.['_preview']?.layers ?? []) as OutputLayer[];
      resolved = legacyPreview.length > 0 ? legacyPreview : topLevelLayers;
    }
  }
  const officialBackup = liveContextState?.playState?.backupSlides === true || officialBackupSlides;
  if (officialBackup) {
    return applyOfficialBackupSlideVisibility(resolved);
  }
  if (liveContextState?.playState?.presentationTransitionInFlight === true) {
    return revealBackupSlidesWhenAgentTrackMissing(resolved);
  }
  return applyOfficialAgentTrackVisibility(resolved);
}

/**
 * Control Current in play: the type-level program (`_preview:stagePlayer` / `_preview:onlinePlayer`)
 * exactly as the players of that type receive it. Visibility follows the same two rules the
 * player's layer renderer applies; no SIDs or content are borrowed from other snapshots.
 */
export function resolveCurrentPlayoutLayers(
  liveContextState: LiveContextStateDto | undefined,
  tab: OutputPreviewTab,
): OutputLayer[] {
  const layers = (liveContextState?.playState?.byPlayer?.[PREVIEW_KEY_BY_TAB[tab]]?.layers ?? []) as OutputLayer[];
  if (layers.length === 0) {
    return layers;
  }
  return liveContextState?.playState?.backupSlides === true
    ? applyOfficialBackupSlideVisibility(layers)
    : applyOfficialAgentTrackVisibility(layers);
}

/**
 * Visible media-agent LiveKit with no SIDs is a dead publisher. Show the hidden stills shield
 * so Current / Broadcast do not sit on a black tile + spinner.
 */
export function revealBackupSlidesWhenAgentTrackMissing(layers: OutputLayer[]): OutputLayer[] {
  const infra = layers.find(
    (layer) =>
      layer.visible !== false &&
      layer.componentId === 'livekitTrackMediaAgent' &&
      typeof layer.itemId === 'string' &&
      layer.itemId.startsWith('mediaAgent:'),
  );
  if (!infra || collectOutputLayerTrackSids(infra).length > 0) {
    return layers;
  }
  const stills = layers.filter(
    (layer) =>
      (layer.componentId === 'imageSequence' || layer.componentId === 'presentationSlides') &&
      typeof layer.itemId === 'string' &&
      layer.itemId.startsWith('media:'),
  );
  if (stills.length === 0) {
    return layers;
  }
  const stillKeys = new Set(stills.map((layer) => `${layer.layerId}:${layer.itemId ?? ''}`));
  return layers.map((layer) => {
    if (layer.layerId === infra.layerId && layer.itemId === infra.itemId) {
      return { ...layer, visible: false, audible: false };
    }
    if (stillKeys.has(`${layer.layerId}:${layer.itemId ?? ''}`)) {
      return { ...layer, visible: true };
    }
    return layer;
  });
}

function isMediaAgentInfraLayer(layer: OutputLayer): boolean {
  return (
    layer.componentId === 'livekitTrackMediaAgent' &&
    typeof layer.itemId === 'string' &&
    layer.itemId.startsWith('mediaAgent:')
  );
}

function layerFileType(layer: OutputLayer): string | undefined {
  const fileType = layer.data?.fileType;
  return typeof fileType === 'string' && fileType.length > 0 ? fileType : undefined;
}

/** Native in-player media — never a PPT backup-stills shield. */
function isNativePlayerMediaFileType(fileType: string | undefined): boolean {
  return (
    fileType === 'pdf' ||
    fileType === 'imageSequence' ||
    fileType === 'video' ||
    fileType === 'image' ||
    fileType === 'audio'
  );
}

/** PPT backup/agent pair only. Unstamped PDF frames must not enter this set. */
function isPresentationShieldLayer(layer: OutputLayer): boolean {
  if (typeof layer.itemId !== 'string' || !layer.itemId.startsWith('media:')) {
    return false;
  }
  if (layer.componentId === 'presentationSlides') {
    return !isNativePlayerMediaFileType(layerFileType(layer));
  }
  return layer.componentId === 'imageSequence' && layerFileType(layer) === 'presentation';
}

function isBackupStillsLayer(layer: OutputLayer): boolean {
  return isPresentationShieldLayer(layer);
}

/** Cue content only — template chrome (no itemId) and `overlay:*` info overlays sit beside the agent track. */
function isCueContentLayer(layer: OutputLayer): boolean {
  const { itemId } = layer;
  return (
    typeof itemId === 'string' &&
    (itemId.startsWith('media:') || itemId.startsWith('composite:') || itemId.startsWith('virtualMedia:'))
  );
}

function isNativeMediaOnAir(layer: OutputLayer): boolean {
  if (layer.visible === false || !isCueContentLayer(layer)) {
    return false;
  }
  if (
    layer.componentId === 'pdf' ||
    layer.componentId === 'video' ||
    layer.componentId === 'image' ||
    layer.componentId === 'audio' ||
    layer.componentId === 'html'
  ) {
    return true;
  }
  if (layer.componentId !== 'imageSequence' && layer.componentId !== 'presentationSlides') {
    return false;
  }
  return !isPresentationShieldLayer(layer);
}

/** Official backup mode: hide leftover agent LiveKit even when SIDs are still on the row. */
export function applyOfficialBackupSlideVisibility(layers: OutputLayer[]): OutputLayer[] {
  const agentTrackVisible = layers.some((layer) => isMediaAgentInfraLayer(layer) && layer.visible !== false);
  const compositeOnAir = layers.some(
    (layer) =>
      layer.visible !== false &&
      ((layer.componentId === 'html' && layer.itemId?.startsWith('composite:')) ||
        layer.itemId?.startsWith('virtualMedia:')),
  );
  // Hidden overflow decks (the next PPT) must stay hidden while Event Info is the cue.
  const revealHiddenStills = agentTrackVisible && !compositeOnAir;
  const stills = layers.filter(
    (layer) =>
      (isPresentationShieldLayer(layer) ||
        (layer.componentId === 'imageSequence' &&
          typeof layer.itemId === 'string' &&
          layer.itemId.startsWith('media:') &&
          !isNativePlayerMediaFileType(layerFileType(layer)))) &&
      (layer.visible !== false || revealHiddenStills),
  );
  const stillKeys = new Set(stills.map((layer) => `${layer.layerId}:${layer.itemId ?? ''}`));
  return layers.map((layer) => {
    if (isMediaAgentInfraLayer(layer)) {
      return { ...layer, visible: false, audible: false };
    }
    if (stillKeys.has(`${layer.layerId}:${layer.itemId ?? ''}`)) {
      return { ...layer, visible: true };
    }
    return layer;
  });
}

/**
 * Official agent mode: if the PPT pair is on air, show the LiveKit row and hide backup stills.
 * Leftover hidden pairs under a PDF stay hidden.
 */
export function applyOfficialAgentTrackVisibility(layers: OutputLayer[]): OutputLayer[] {
  if (layers.some(isNativeMediaOnAir)) {
    return layers.map((layer) =>
      isMediaAgentInfraLayer(layer) ? { ...layer, visible: false, audible: false } : layer,
    );
  }
  const infra = layers.find(isMediaAgentInfraLayer);
  const stills = layers.filter(isBackupStillsLayer);
  if (!infra || stills.length === 0) {
    return layers;
  }
  const pairOnAir = infra.visible !== false || stills.some((layer) => layer.visible !== false);
  if (!pairOnAir) {
    return layers;
  }
  const stillKeys = new Set(stills.map((layer) => `${layer.layerId}:${layer.itemId ?? ''}`));
  return layers.map((layer) => {
    if (layer.layerId === infra.layerId && layer.itemId === infra.itemId) {
      return { ...layer, visible: true };
    }
    if (stillKeys.has(`${layer.layerId}:${layer.itemId ?? ''}`)) {
      return { ...layer, visible: false, audible: false };
    }
    return layer;
  });
}

/**
 * True when Current / players are already on backup stills (BE suppress or local reveal).
 * Does not treat a leftover hidden agent row over a PDF as fallback.
 */
export function layersIndicateImageSequenceFallback(
  layers: Array<Pick<OutputLayer, 'componentId' | 'itemId' | 'visible' | 'trackSid' | 'trackSids'>>,
  options?: { currentItemId?: string; activeAgentDeckMediaId?: string },
): boolean {
  const infra = layers.find(
    (layer) =>
      layer.componentId === 'livekitTrackMediaAgent' &&
      typeof layer.itemId === 'string' &&
      layer.itemId.startsWith('mediaAgent:'),
  );
  const stills = layers.filter(
    (layer) =>
      (layer.componentId === 'imageSequence' || layer.componentId === 'presentationSlides') &&
      typeof layer.itemId === 'string' &&
      layer.itemId.startsWith('media:'),
  );
  if (!infra || stills.length === 0) {
    return false;
  }
  const deadVisibleInfra = infra.visible !== false && collectOutputLayerTrackSids(infra).length === 0;
  if (deadVisibleInfra) {
    return true;
  }
  const deckId = options?.activeAgentDeckMediaId ?? options?.currentItemId;
  if (!deckId || infra.visible !== false) {
    return false;
  }
  return stills.some((layer) => layer.visible !== false && layer.itemId === deckId);
}

export function resolveSimulationPlayerType(tab: OutputPreviewTab): 'stagePlayer' | 'roomPlayer' | 'onlinePlayer' {
  return tab === 'stageRoom' ? 'stagePlayer' : 'onlinePlayer';
}

/** True when at least one layer would render in preview (visible or preload). */
export function hasRenderablePreviewLayers(layers: OutputLayer[]): boolean {
  return layers.some((layer) => layer.visible !== false || layer.preload === true);
}

function layerHasImageSequenceUrls(layer: OutputLayer): boolean {
  const raw = layer.data?.imageSequenceUrls ?? layer.data?.imageUrls;
  if (!raw) {
    return false;
  }
  if (Array.isArray(raw)) {
    return raw.length > 0;
  }
  if (typeof raw !== 'string' || raw.length < 2) {
    return false;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    // Malformed payload is not paint-ready — fall back to HTTP Next preview.
    return false;
  }
}

const CUE_ITEM_ID_PREFIXES = ['media:', 'composite:', 'virtualMedia:'] as const;

function isCueItemId(value: string): boolean {
  return CUE_ITEM_ID_PREFIXES.some((prefix) => value.startsWith(prefix));
}

function collectCueItemIdsFromPreviewLayers(layers: OutputLayer[]): Set<string> {
  const ids = new Set<string>();
  for (const layer of layers) {
    if (layer.visible === false && layer.preload !== true) {
      continue;
    }
    if (typeof layer.itemId === 'string' && isCueItemId(layer.itemId)) {
      ids.add(layer.itemId);
    }
    const mediaId = layer.data?.mediaId;
    if (typeof mediaId === 'string' && mediaId.length > 0) {
      ids.add(mediaId.startsWith('media:') ? mediaId : `media:${mediaId}`);
    }
  }
  return ids;
}

/**
 * True when mounted Next layers belong to `nextItemId`.
 * HTML / LiveKit-only composites have no cue itemId — those still match so speaker-only Next can paint.
 * A leftover Image/video from a different media item must not match a PPT Next.
 */
export function nextPreviewLayersMatchTarget(layers: OutputLayer[], nextItemId: string): boolean {
  if (!nextItemId) {
    return layers.length === 0;
  }
  const ids = collectCueItemIdsFromPreviewLayers(layers);
  if (ids.size === 0) {
    return hasRenderablePreviewLayers(layers);
  }
  return [...ids].every((id) => cueItemIdEqualsTarget(id, nextItemId));
}

function cueItemIdEqualsTarget(id: string, nextItemId: string): boolean {
  if (id === nextItemId) {
    return true;
  }
  if (nextItemId.startsWith('media:')) {
    const bare = nextItemId.slice('media:'.length);
    return id === bare || id === `media:${bare}`;
  }
  return id === `media:${nextItemId}`;
}

const PERSISTENT_PREVIEW_ITEM_PREFIXES = [
  'roomCam:',
  'remoteSpeaker:',
  'screenshare:',
  'mediaAgent:',
  'overlay:',
] as const;

/** Drop leftover media from a prior Current when painting insert-resume Next. */
export function filterOutputLayersToCueItem(layers: OutputLayer[], itemId: string): OutputLayer[] {
  return layers.filter((layer) => {
    if (!layer.itemId) {
      return true;
    }
    if (cueItemIdEqualsTarget(layer.itemId, itemId)) {
      return true;
    }
    return PERSISTENT_PREVIEW_ITEM_PREFIXES.some((prefix) => layer.itemId!.startsWith(prefix));
  });
}

/**
 * Whether BE look-ahead can paint Control Next for `nextItemId`.
 * Identity is gated by look-ahead meta before this runs. Sequenced rows still need frame URLs;
 * speaker-only / HTML composites may have no layer whose itemId equals Next.
 */
function layerIsShownInNextPreview(layer: OutputLayer): boolean {
  if (layer.visible === false || layer.preload === true) {
    return false;
  }
  if (layer.componentId === 'html') {
    return String(layer.data?.html ?? '').trim().length > 0;
  }
  return true;
}

export function lookAheadLayersUsableForNextPreview(layers: OutputLayer[], nextItemId: string): boolean {
  // Hidden preload rows and empty HTML composites must not block the simulation-preview fetch.
  // Accepting them leaves Next black until the next click refetches a visible snapshot.
  if (!layers.some(layerIsShownInNextPreview)) {
    return false;
  }
  if (!hasRenderablePreviewLayers(layers)) {
    return false;
  }
  if (!nextPreviewLayersMatchTarget(layers, nextItemId)) {
    return false;
  }
  const bareMediaId = nextItemId.startsWith('media:') ? nextItemId.slice('media:'.length) : undefined;
  const sequencedForNext = layers.filter((layer) => {
    if (
      layer.componentId !== 'imageSequence' &&
      layer.componentId !== 'presentationSlides' &&
      layer.componentId !== 'pdf'
    ) {
      return false;
    }
    return (
      layer.itemId === nextItemId ||
      (bareMediaId != null && (layer.data?.mediaId === bareMediaId || layer.itemId === `media:${bareMediaId}`))
    );
  });
  if (sequencedForNext.length > 0) {
    return sequencedForNext.some((layer) =>
      layer.componentId === 'pdf'
        ? Boolean(layer.data?.url || layer.data?.pdfUrl || layer.data?.src)
        : layerHasImageSequenceUrls(layer),
    );
  }
  const imagesForNext = layers.filter(
    (layer) =>
      layer.componentId === 'image' &&
      layer.visible !== false &&
      (layer.itemId === nextItemId || (bareMediaId != null && layer.itemId === `media:${bareMediaId}`)),
  );
  if (imagesForNext.length > 0) {
    return imagesForNext.some((layer) => Boolean(layer.data?.src || layer.data?.imageUrl || layer.data?.url));
  }
  return true;
}

/** True when this Next snapshot still points at an image URL that already failed to load. */
export function previewLayersUseFailedImageUrl(layers: OutputLayer[], failedUrls: readonly string[]): boolean {
  if (failedUrls.length === 0) {
    return false;
  }
  return layers.some((layer) => {
    if (layer.componentId !== 'image' || layer.visible === false) {
      return false;
    }
    const url = layer.data?.src || layer.data?.imageUrl || layer.data?.url || '';
    return url.length > 0 && failedUrls.includes(url);
  });
}

function mediaIdsMatch(layerItemId: string | undefined, nextItemId: string): boolean {
  if (!layerItemId) {
    return false;
  }
  if (layerItemId === nextItemId) {
    return true;
  }
  const bare = nextItemId.startsWith('media:') ? nextItemId.slice('media:'.length) : nextItemId;
  return layerItemId === bare || layerItemId === `media:${bare}`;
}

function deckLayerCanPaint(layer: OutputLayer, nextItemId: string): boolean {
  const mediaId = typeof layer.data?.mediaId === 'string' ? layer.data.mediaId : undefined;
  if (!mediaIdsMatch(layer.itemId, nextItemId) && !mediaIdsMatch(mediaId, nextItemId)) {
    return false;
  }
  if (layer.componentId === 'imageSequence' || layer.componentId === 'presentationSlides') {
    return layerHasImageSequenceUrls(layer);
  }
  if (layer.componentId === 'pdf') {
    return Boolean(layer.data?.url || layer.data?.pdfUrl || layer.data?.src);
  }
  return false;
}

function dedupeShownDeckLayers(layers: OutputLayer[]): OutputLayer[] {
  const byId = new Map<number, OutputLayer>();
  for (const layer of layers) {
    if (!byId.has(layer.layerId)) {
      byId.set(layer.layerId, layer);
    }
  }
  return [...byId.values()];
}

/**
 * One snapshot for this output tab. Stage and Broadcast copies of the same deck
 * must not be stacked; renderers key every layer by layerId.
 */
function nextDeckSourceKeys(
  byPlayer: NonNullable<NonNullable<LiveContextStateDto['playState']>['byPlayer']>,
  tab: OutputPreviewTab,
  displayPlayerIds: string[] | undefined,
): string[] {
  const keys = [PREVIEW_KEY_BY_TAB[tab]];
  if (displayPlayerIds != null) {
    return [...keys, ...displayPlayerIds];
  }
  const physical = Object.keys(byPlayer).find((key) => !isOperatorPlayStateByPlayerKey(key));
  return physical ? [...keys, physical] : keys;
}

/**
 * The first deck is often already on the players and confidence before Control Next
 * finishes its own preview fetch. Reuse that tab's picture instead of a spinner.
 */
export function resolveAlreadyShownNextDeckLayers(
  liveContextState: LiveContextStateDto | undefined,
  nextItemId: string | undefined,
  options?: { tab?: OutputPreviewTab; displayPlayerIds?: string[] },
): OutputLayer[] | null {
  const byPlayer = liveContextState?.playState?.byPlayer;
  if (!nextItemId?.startsWith('media:') || !byPlayer) {
    return null;
  }
  const tab = options?.tab ?? 'stageRoom';
  const bare = nextItemId.slice('media:'.length);
  const active = liveContextState.playState?.activeAgentDeckMediaId;
  const agentAlreadyOnThisDeck = active === nextItemId || active === bare || active === `media:${bare}`;
  const sourceKeys = nextDeckSourceKeys(byPlayer, tab, options?.displayPlayerIds);

  for (const key of sourceKeys) {
    const deckStills = dedupeShownDeckLayers(
      ((byPlayer[key]?.layers ?? []) as OutputLayer[])
        .filter((layer) => deckLayerCanPaint(layer, nextItemId))
        .map((layer) => ({ ...layer, visible: true, preload: false })),
    );
    if (deckStills.length > 0 && lookAheadLayersUsableForNextPreview(deckStills, nextItemId)) {
      return deckStills;
    }
  }

  for (const key of sourceKeys) {
    const track = ((byPlayer[key]?.layers ?? []) as OutputLayer[]).find(
      (layer) =>
        agentAlreadyOnThisDeck &&
        layer.componentId === 'livekitTrackMediaAgent' &&
        layer.visible !== false &&
        collectOutputLayerTrackSids(layer).length > 0,
    );
    if (!track) {
      continue;
    }
    const agentTrack = { ...track, itemId: nextItemId, visible: true, preload: false };
    if (lookAheadLayersUsableForNextPreview([agentTrack], nextItemId)) {
      return [agentTrack];
    }
  }
  return null;
}

/**
 * Current must show the cue's on-air item. A go-live preload can push the next deck into the
 * same snapshot; painting that deck makes Current flicker until the composite arrives.
 */
export function omitOffAirCueMedia(layers: OutputLayer[], cueItemId: string | undefined): OutputLayer[] {
  if (!cueItemId) {
    return layers;
  }
  const foreignOnAir = layers.some(
    (layer) =>
      layer.visible !== false &&
      typeof layer.itemId === 'string' &&
      isCueItemId(layer.itemId) &&
      !cueItemIdEqualsTarget(layer.itemId, cueItemId),
  );
  if (!foreignOnAir) {
    return layers;
  }
  const kept = layers.filter((layer) => {
    if (!layer.itemId || !isCueItemId(layer.itemId)) {
      return true;
    }
    if (cueItemIdEqualsTarget(layer.itemId, cueItemId)) {
      return true;
    }
    return PERSISTENT_PREVIEW_ITEM_PREFIXES.some((prefix) => layer.itemId!.startsWith(prefix));
  });
  const hasOnAir = kept.some(
    (layer) =>
      layer.visible !== false && typeof layer.itemId === 'string' && cueItemIdEqualsTarget(layer.itemId, cueItemId),
  );
  if (hasOnAir) {
    return kept;
  }
  return kept.filter((layer) => !layer.itemId || !isCueItemId(layer.itemId));
}

/** True when Control Current is showing this cue item and not a second media item beside it. */
export function currentPreviewShowsCueItem(
  liveContextState: LiveContextStateDto | undefined,
  itemId: string | undefined,
): boolean {
  if (!itemId) {
    return false;
  }
  const shown = resolveCurrentPlayoutLayers(liveContextState, 'stageRoom').filter(
    (layer) => layer.visible !== false && layer.preload !== true,
  );
  const ids = collectCueItemIdsFromPreviewLayers(shown);
  if (ids.size === 0) {
    const activeDeck = liveContextState?.playState?.activeAgentDeckMediaId;
    return (
      typeof activeDeck === 'string' &&
      cueItemIdEqualsTarget(activeDeck, itemId) &&
      shown.some(
        (layer) => layer.componentId === 'livekitTrackMediaAgent' && collectOutputLayerTrackSids(layer).length > 0,
      )
    );
  }
  return [...ids].every((id) => cueItemIdEqualsTarget(id, itemId));
}

/** Sequential take: Next+1 already holds cue Next, `_lookahead:*` has not been promoted yet. */
export function isSequentialLookAheadPromotePending(params: {
  next?: { itemId?: string; slotId?: string; scope?: string } | null;
  lookAhead?: { itemId?: string; slotId?: string; scope?: string } | null;
  lookAhead2?: { itemId?: string; slotId?: string; scope?: string } | null;
}): boolean {
  return (
    lookAheadMetaMatchesNextTarget(params.lookAhead2, params.next) &&
    !lookAheadMetaMatchesNextTarget(params.lookAhead, params.next)
  );
}

/**
 * Visible LiveKit layers are present but still lack track SIDs — preview should refetch live context
 * until the backend recompute path fills them (players get the same data via update-state sooner).
 * A synthetic room-cam placeholder never receives a SID.
 */
export function livePreviewLayersMissingTrackSids(layers: OutputLayer[]): boolean {
  return layers.some((layer) => {
    if (layer.visible === false) {
      return false;
    }
    if (!isLiveKitPreviewLayer(layer) || isSyntheticRoomCamPlaceholder(layer.itemId)) {
      return false;
    }
    return collectOutputLayerTrackSids(layer).length === 0;
  });
}

/** LiveKit layers that Next look-ahead may show as local-cam placeholders in simulation. */
const LOOK_AHEAD_LIVEKIT_ATTACH_COMPONENT_IDS = new Set([
  'livekitTrackRoomCam',
  'livekitTrackRoomCamGrid',
  'livekitTrackRemoteSpeaker',
]);

function donorKey(layer: OutputLayer): string {
  return `${layer.componentId}:${layer.layerId}:${layer.itemId ?? ''}`;
}

/**
 * Keep Next look-ahead layout/media from the simulation-preview API, but attach On Air
 * LiveKit track SIDs onto matching roomCam/speaker slots so Control Next can render the
 * real LiveKit video instead of the local webcam placeholder.
 */
export function attachLiveKitTracksToLookAheadPreviewLayers(
  lookAheadLayers: OutputLayer[],
  liveLayers: OutputLayer[],
): OutputLayer[] {
  const donorsByComponent = new Map<string, OutputLayer[]>();
  for (const layer of liveLayers) {
    if (!LOOK_AHEAD_LIVEKIT_ATTACH_COMPONENT_IDS.has(layer.componentId)) {
      continue;
    }
    if (collectOutputLayerTrackSids(layer).length === 0) {
      continue;
    }
    const list = donorsByComponent.get(layer.componentId) ?? [];
    list.push(layer);
    donorsByComponent.set(layer.componentId, list);
  }
  if (donorsByComponent.size === 0) {
    return lookAheadLayers;
  }

  const usedDonors = new Set<string>();

  return lookAheadLayers.map((layer) => {
    if (!LOOK_AHEAD_LIVEKIT_ATTACH_COMPONENT_IDS.has(layer.componentId)) {
      return layer;
    }
    if (collectOutputLayerTrackSids(layer).length > 0) {
      return layer;
    }

    const donors = donorsByComponent.get(layer.componentId) ?? [];
    const byItem =
      layer.itemId != null ? donors.find((d) => d.itemId === layer.itemId && !usedDonors.has(donorKey(d))) : undefined;
    const donor = byItem ?? donors.find((d) => !usedDonors.has(donorKey(d)));
    if (!donor) {
      return layer;
    }

    usedDonors.add(donorKey(donor));
    const trackSids = collectOutputLayerTrackSids(donor);
    return {
      ...layer,
      trackSids,
      trackSid: donor.trackSid ?? trackSids[0],
    };
  });
}

export { PLAYER_TYPE_BY_TAB };
